import type {
  StoreSpecial as V3StoreSpecial,
  ReservationCartProduct,
} from '@iheartjane/data-access';
import type { PriceId } from '@iheartjane/types';
import round from 'lodash-es/round';

import type { AlgoliaProduct } from '@jane/search/types';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, StoreSpecial } from '@jane/shared/models';
import type { StoreSpecial as ZStoreSpecial } from '@jane/shared/types';

import { defaultWeightIdForMenuProduct } from '../storeMenu';
import { maybeParseFloat } from './maybeParseFloat';

/**
 * The per-weight price fields the unit-price helpers read. Structural so one
 * signature serves every menu/cart/search product — v1 money strings and v3
 * numbers alike.
 *
 * `object &` is load-bearing: without it this is a weak type, and TypeScript
 * rejects products carrying none of these keys (a bare `Product`). Such callers
 * exist and read `undefined` today.
 */
export type WeightPriced = object &
  Partial<
    Record<
      `price_${PriceId}` | `discounted_price_${PriceId}`,
      string | number | null
    >
  >;

/**
 * `0` is a legitimate discounted price (BOGO-free, 100% off). The v1 wire sends
 * it as the string `"0.0"` and the v3 cart as the number `0`, so a truthiness
 * test would accept one and reject the other, silently re-rendering a free line
 * at full price.
 */
const hasPrice = (value: string | number | null | undefined): boolean =>
  value !== null && value !== undefined && value !== '';

export const getActualUnitPrice = (
  menuProduct: WeightPriced,
  priceId: PriceId,
  // Only truthiness-tested, never read, so any special shape serves — but
  // `object` still rejects a primitive passed by mistake.
  applicableSpecial?: object
): number | undefined => {
  const discountedPrice = menuProduct[`discounted_price_${priceId}`];

  return maybeParseFloat(
    applicableSpecial && hasPrice(discountedPrice)
      ? discountedPrice
      : menuProduct[`price_${priceId}`]
  );
};

export const getUnitPrice = (
  menuProduct: WeightPriced,
  priceId: PriceId
): number | undefined => maybeParseFloat(menuProduct[`price_${priceId}`]);

export const getDiscountedUnitPrice = (
  menuProduct: WeightPriced,
  priceId: PriceId
): number | undefined =>
  maybeParseFloat(menuProduct[`discounted_price_${priceId}`]);

export const isDiscountSupported = (
  menuProduct: MenuProduct,
  priceId: PriceId,
  supportedSpecials?: (StoreSpecial | ZStoreSpecial | V3StoreSpecial)[]
): boolean | undefined => {
  const specialPrice = menuProduct[`special_price_${priceId}`];
  const specialId = menuProduct.special_id;

  const supportedSpecialIds =
    supportedSpecials && supportedSpecials?.map(({ id }) => id);

  const hasSpecialPrice =
    specialPrice?.special_id &&
    supportedSpecialIds?.includes(specialPrice?.special_id);

  const hasValidSpecialId =
    specialId && supportedSpecialIds?.includes(specialId);

  return Boolean(hasSpecialPrice || hasValidSpecialId);
};

export const calculateTotalPrice = (
  unitPrice: number | undefined,
  count: number
) => {
  if (unitPrice === undefined) return undefined;

  return unitPrice * count;
};

export const getOriginalAndDiscountedTotalPrices = ({
  menuProduct,
  count = 1,
  priceId,
}: {
  count?: number;
  menuProduct: MenuProduct | AlgoliaProduct;
  priceId?: PriceId;
}) => {
  const selectedWeight =
    priceId || defaultWeightIdForMenuProduct({ menuProduct });
  const originalUnitPrice = getUnitPrice(menuProduct, selectedWeight);
  const discountUnitPrice = getDiscountedUnitPrice(menuProduct, selectedWeight);
  const originalPrice = calculateTotalPrice(originalUnitPrice, count);
  const discountedPrice = calculateTotalPrice(discountUnitPrice, count);

  return { originalPrice, discountedPrice };
};

export const getTotalOriginalAndDiscountedPrices = ({
  menuProduct,
  cartProduct,
}: {
  cartProduct: ReservationCartProduct[];
  menuProduct: MenuProduct | AlgoliaProduct;
}) => {
  const withPrices = cartProduct.map((product) => {
    const originalUnitPrice = getUnitPrice(menuProduct, product.price_id);
    const discountUnitPrice = getDiscountedUnitPrice(
      menuProduct,
      product.price_id
    );
    return {
      originalPrice: calculateTotalPrice(originalUnitPrice, product.count),
      discountedPrice: calculateTotalPrice(discountUnitPrice, product.count),
    };
  });

  const { totalOriginalPrice, totalDiscountedPrice } = withPrices.reduce(
    (prev, curr) => ({
      totalOriginalPrice: prev.totalOriginalPrice + (curr.originalPrice || 0),
      totalDiscountedPrice:
        prev.totalDiscountedPrice + (curr.discountedPrice || 0),
    }),
    { totalOriginalPrice: 0, totalDiscountedPrice: 0 }
  );

  return { totalOriginalPrice, totalDiscountedPrice };
};

export const calculateDiscount = (
  originalPrice: number,
  discountedPrice: number
) => round(originalPrice - discountedPrice, 2);

export const calculateDiscountPercentage = (
  originalPrice: number | undefined,
  discountedPrice: number | undefined
) => {
  if (!discountedPrice || !originalPrice || discountedPrice === originalPrice) {
    return null;
  }
  return round(((originalPrice - discountedPrice) / originalPrice) * 100);
};
